"use client";
import * as React from "react";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
  align?: "left" | "right" | "center";
  width?: string;
  sortValue?: (row: T) => string | number;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  searchKeys?: (keyof T | ((row: T) => string))[];
  searchPlaceholder?: string;
  pageSize?: number;
  maxHeight?: string;
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string;
  emptyMessage?: string;
  toolbar?: React.ReactNode;
  className?: string;
  initialSort?: { key: string; dir: "asc" | "desc" };
}

export function DataTable<T>({
  columns, data, searchKeys, searchPlaceholder = "Search...",
  pageSize = 10, maxHeight = "60vh", onRowClick, rowKey,
  emptyMessage = "No records found", toolbar, className, initialSort
}: DataTableProps<T>) {
  const [query, setQuery] = React.useState("");
  const [sort, setSort] = React.useState<{ key: string; dir: "asc" | "desc" } | null>(initialSort ?? null);
  const [page, setPage] = React.useState(0);

  const filtered = React.useMemo(() => {
    if (!query || !searchKeys || searchKeys.length === 0) return data;
    const q = query.toLowerCase();
    return data.filter(row => searchKeys.some(k => {
      const v = typeof k === "function" ? k(row) : String((row as Record<string, unknown>)[k as string] ?? "");
      return v.toLowerCase().includes(q);
    }));
  }, [data, query, searchKeys]);

  const sorted = React.useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find(c => c.key === sort.key);
    if (!col) return filtered;
    const getVal = (row: T) => col.sortValue ? col.sortValue(row) : String((row as Record<string, unknown>)[col.key] ?? "");
    return [...filtered].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      // M1 fix: detect ISO dates (YYYY-MM-DD) and sort chronologically
      const isoDate = /^\d{4}-\d{2}-\d{2}/;
      if (isoDate.test(String(va)) && isoDate.test(String(vb))) {
        return sort.dir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
      }
      const na = typeof va === "number" ? va : parseFloat(va as string);
      const nb = typeof vb === "number" ? vb : parseFloat(vb as string);
      if (!isNaN(na) && !isNaN(nb) && na !== 0 && nb !== 0) return sort.dir === "asc" ? na - nb : nb - na;
      return sort.dir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [filtered, sort, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageData = sorted.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const toggleSort = (key: string) => {
    setSort(prev => prev?.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        {searchKeys && searchKeys.length > 0 ? (
          <div className="relative w-full sm:max-w-xs">
            <Search className="text-muted-foreground absolute left-2.5 top-1/2 size-4 -translate-y-1/2" />
            <Input value={query} onChange={e => { setQuery(e.target.value); setPage(0); }} placeholder={searchPlaceholder} className="pl-8" />
          </div>
        ) : <div />}
        {toolbar && <div className="flex flex-wrap items-center gap-2">{toolbar}</div>}
      </div>
      <div className="overflow-x-auto rounded-md border" style={{ maxHeight }}>
        <table className="w-full text-sm">
          <thead className="bg-muted/50 sticky top-0 z-10">
            <tr className="border-b">
              {columns.map(c => (
                <th key={c.key} className={cn("px-3 py-2.5 text-xs font-semibold uppercase tracking-wide",
                  c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left")}
                  style={{ width: c.width }}>
                  {c.sortable ? (
                    <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort(c.key)}>
                      {c.header}
                      {sort?.key === c.key ? (sort.dir === "asc" ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />) : null}
                    </button>
                  ) : c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr><td colSpan={columns.length} className="text-muted-foreground px-3 py-8 text-center">{emptyMessage}</td></tr>
            ) : pageData.map(row => (
              <tr key={rowKey(row)} className={cn("border-b last:border-0 hover:bg-muted/40", onRowClick && "cursor-pointer")}
                onClick={() => onRowClick?.(row)}>
                {columns.map(c => (
                  <td key={c.key} className={cn("px-3 py-2 align-middle",
                    c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left")}>
                    {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-muted-foreground text-xs">
          Showing {sorted.length === 0 ? 0 : safePage * pageSize + 1}-{Math.min((safePage + 1) * pageSize, sorted.length)} of {sorted.length}
        </p>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" disabled={safePage === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
            <ChevronLeft className="size-4" /> Prev
          </Button>
          <span className="text-muted-foreground px-2 text-xs">Page {safePage + 1} of {totalPages}</span>
          <Button size="sm" variant="outline" disabled={safePage >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>
            Next <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
